/**
 * WhatsAppConexionPage — `/activa/whatsapp`
 *
 * El circuito de conexión de WhatsApp por QR:
 *   crear línea → conectar → escanear el QR con el WhatsApp del local →
 *   línea "activa" → probar envío. Multi-número. Todo contra el backend real.
 *
 * En modo simulado el QR es un placeholder y la línea "se escanea" sola a los
 * segundos (para probar el circuito sin teléfono). Con WA_TRANSPORT=openwa,
 * el backend arranca open-wa y el QR es real y escaneable.
 */

import { useEffect, useRef, useState } from "react";
import {
  Smartphone, Plus, Loader2, CheckCircle2, QrCode, Send, LogOut, ShieldAlert, RefreshCw, KeyRound,
  Check, CheckCheck, XCircle, Clock, Trash2, MessageSquare, ChevronDown,
} from "lucide-react";
import { activaApi } from "../api";
import { etiquetaEstadoLinea } from "../utils/estadoLinea";
import { resultadoPruebaEnvio } from "../utils/resultadoPrueba";

/** ticks estilo WhatsApp según el estado de entrega de un mensaje. */
function EstadoTick({ estado }: { estado: string }) {
  if (estado === "leido") return <span className="inline-flex items-center gap-1 text-sky-500"><CheckCheck size={15} /> leído</span>;
  if (estado === "entregado") return <span className="inline-flex items-center gap-1 text-slate-500"><CheckCheck size={15} /> entregado</span>;
  if (estado === "fallido") return <span className="inline-flex items-center gap-1 text-red-500"><XCircle size={14} /> falló</span>;
  if (estado === "enviado") return <span className="inline-flex items-center gap-1 text-slate-400"><Check size={15} /> enviado</span>;
  return <span className="inline-flex items-center gap-1 text-slate-400"><Clock size={13} /> pendiente</span>;
}

const badge: Record<string, { cls: string; label: string }> = {
  desconectada: { cls: "bg-slate-100 text-slate-500", label: etiquetaEstadoLinea("desconectada") },
  conectando: { cls: "bg-amber-50 text-amber-600", label: etiquetaEstadoLinea("conectando") },
  qr_pendiente: { cls: "bg-violet-50 text-violet-600", label: etiquetaEstadoLinea("qr_pendiente") },
  calentando: { cls: "bg-amber-50 text-amber-600", label: etiquetaEstadoLinea("calentando") },
  activa: { cls: "bg-emerald-50 text-emerald-600", label: etiquetaEstadoLinea("activa") },
  bloqueada: { cls: "bg-red-50 text-red-600", label: etiquetaEstadoLinea("bloqueada") },
};

export default function WhatsAppConexionPage() {
  const [lines, setLines] = useState<any[]>([]);
  const [active, setActive] = useState<any | null>(null); // línea en proceso de conexión (con QR o código)
  /** id de la línea recién creada que todavía no se vinculó: si se cancela, se borra */
  const [provisional, setProvisional] = useState("");
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState("");
  // por línea (id → valor): con varias líneas activas, cada input y su resultado son independientes.
  const [testTo, setTestTo] = useState<Record<string, string>>({});
  const [testResult, setTestResult] = useState<Record<string, string>>({});
  const [stats, setStats] = useState<any>(null); // estadísticas de entrega/lectura de las pruebas
  /**
   * Respuestas que llegaron a cada línea sin campaña atribuible (id de línea → filas, o el estado
   * de la carga). Se piden recién al abrir el bloque: casi siempre está vacío (la línea es de
   * promos) y son datos personales de gente a la que quizás nunca se le escribió.
   */
  const [sinCampana, setSinCampana] = useState<Record<string, any[] | "cargando" | "error">>({});
  const [sinCampanaAbierta, setSinCampanaAbierta] = useState<Record<string, boolean>>({});
  const poll = useRef<any>(null);
  const statsPoll = useRef<any>(null);

  const load = async () => {
    try { const r = await activaApi.lines(); setLines(r.data || []); } catch { /* backend abajo */ }
  };
  const loadStats = async () => {
    try { const r = await activaApi.testStats(); setStats(r.data || null); } catch { /* backend abajo */ }
  };
  useEffect(() => {
    load(); loadStats();
    // refresco periódico de las pruebas: así el "entregado → leído" aparece solo cuando abren el mensaje.
    statsPoll.current = setInterval(loadStats, 5000);
    return () => { clearInterval(poll.current); clearInterval(statsPoll.current); };
  }, []);

  /**
   * "Agregar línea" ES conectar un número: abre la vinculación de una.
   *
   * Antes sólo creaba el registro y volvía a la lista, así que cada click dejaba una
   * "Línea 3 · sin vincular" muerta que además no se podía borrar. La línea se crea igual
   * (el backend necesita un id para colgar la sesión del QR), pero es PROVISIONAL: si el
   * operador cancela sin vincular, se borra. Y la lista sólo muestra números que existieron
   * de verdad.
   */
  const addLine = async () => {
    setLoading(true);
    try {
      const r = await activaApi.createLine("Línea " + (lines.length + 1));
      if (!r?.data?.id) return;
      setProvisional(r.data.id);
      startConnect(r.data); // directo a elegir método y escanear
    } finally { setLoading(false); }
  };

  /** Cierra la vinculación. Si la línea era provisional y no llegó a vincularse, no queda. */
  const cancelarConexion = async () => {
    clearInterval(poll.current);
    const id = active?.id;
    setActive(null);
    if (id && provisional === id) {
      await activaApi.deleteLine(id).catch(() => {});
      setProvisional("");
    }
    await load();
  };

  /** Sacar una línea ya vinculada: cierra la sesión y la borra. */
  const borrarLinea = async (line: any) => {
    if (!window.confirm(`¿Sacar "${line.nombre}"${line.numero ? ` (${line.numero})` : ""}? Se cierra la sesión de WhatsApp y deja de usarse para enviar.`)) return;
    setLoading(true);
    try { await activaApi.deleteLine(line.id).catch(() => {}); await load(); } finally { setLoading(false); }
  };

  // Poll compartido: refresca la línea en curso hasta que quede activa o vuelva a desconectada.
  const startPoll = (lineId: string) => {
    clearInterval(poll.current);
    poll.current = setInterval(async () => {
      const r = await activaApi.getLine(lineId).catch(() => null);
      if (!r?.data) return;
      setActive(r.data);
      if (r.data.estado === "activa" || r.data.estado === "desconectada") {
        clearInterval(poll.current);
        // se vinculó: ya es una línea de verdad, deja de ser provisional (no se borra al cerrar)
        if (r.data.estado === "activa") setProvisional("");
        await load();
        if (r.data.estado === "activa") {
          setTimeout(() => setActive(null), 1800);
        } else {
          // El código venció / se cortó antes de vincular: volvemos al selector con el número
          // ya cargado y el motivo, para que el usuario regenere el código de una.
          setActive((a: any) => (a ? { ...a, estado: "elegir", qr: "", pairCode: "", lastError: r.data.lastError || "" } : a));
        }
      }
    }, 1500);
  };

  // Abre el panel de conexión pidiendo elegir método (código o QR) para esta línea.
  // Limpia cualquier poll en curso: si no, el poll de OTRA línea seguiría vivo y pisaría este panel
  // (borrando el número tipeado) al siguiente tick.
  const startConnect = (line: any) => { clearInterval(poll.current); setActive({ ...line, estado: "elegir", qr: "", pairCode: "" }); setPhone(""); };

  // Vinculación por CÓDIGO de teléfono — método robusto (el QR expira cada ~20s).
  const pair = async (line: any) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10) { setActive({ ...line, estado: "elegir", qr: "", pairCode: "", lastError: "Ingresá el número con código de país, ej: 5491154596266" }); return; }
    setActive({ ...line, estado: "conectando", qr: "", pairCode: "" });
    await activaApi.pairLine(line.id, digits).catch(() => {});
    startPoll(line.id);
  };

  // Vinculación por QR clásico.
  const connectQr = async (line: any) => {
    setActive({ ...line, estado: "conectando", qr: "", pairCode: "" });
    await activaApi.connectLine(line.id).catch(() => {});
    startPoll(line.id);
  };

  /** Abre/cierra la bandeja de respuestas sin campaña de una línea; la primera vez la carga. */
  const toggleSinCampana = async (lineId: string) => {
    const abrir = !sinCampanaAbierta[lineId];
    setSinCampanaAbierta((p) => ({ ...p, [lineId]: abrir }));
    if (!abrir || Array.isArray(sinCampana[lineId])) return;
    setSinCampana((p) => ({ ...p, [lineId]: "cargando" }));
    try {
      const r = await activaApi.lineResponses(lineId);
      setSinCampana((p) => ({ ...p, [lineId]: Array.isArray(r?.data) ? r.data : [] }));
    } catch {
      setSinCampana((p) => ({ ...p, [lineId]: "error" }));
    }
  };

  // Cierra la sesión y borra las credenciales guardadas: volver a usar el número exige escanear el
  // QR de nuevo. Se confirma ANTES de cortar el polling, así cancelar no deja nada a medias.
  const disconnect = async (line: any) => {
    if (!window.confirm(`¿Desconectar "${line.nombre}"? Se cierra la sesión de WhatsApp en este número y para volver a usarla hay que escanear el QR de nuevo.`)) return;
    clearInterval(poll.current);
    await activaApi.disconnectLine(line.id).catch(() => {});
    setActive(null);
    await load();
  };

  const test = async (line: any) => {
    const to = (testTo[line.id] || "").trim();
    if (!to) { setTestResult((p) => ({ ...p, [line.id]: "Ingresá un número" })); return; }
    setTestResult((p) => ({ ...p, [line.id]: "enviando…" }));
    const r = await activaApi.testSend(line.id, to, "Mensaje de prueba de DEENEX Activa ✅").catch((e: any) => ({ data: { ok: false, error: e?.message } }));
    // La decisión vive en resultadoPruebaEnvio() (utils/resultadoPrueba.ts, con tests). Ahí está
    // el guard del 422 PRIMERO: `req()` (api.ts) no tira excepción con un 422 y `fail()` deja el
    // mensaje en el nivel raíz de la respuesta, nunca bajo `r.data` — mirar sólo `r.data?.ok` daba
    // "✓ enviado" sobre un envío que el backend rechazó (ej: número en la lista de no-contactar).
    setTestResult((p) => ({ ...p, [line.id]: resultadoPruebaEnvio(r) }));
    loadStats(); // el envío se trackea: refrescamos la tabla de pruebas
  };

  return (
    <div className="min-h-screen p-6 md:p-8 animate-fade-in">
      <div className="mx-auto max-w-4xl space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700">WhatsApp · QR</span>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-2 tracking-tight">Líneas de WhatsApp por QR</h1>
            <p className="text-sm text-slate-500 mt-1">Escaneá un QR con un número dedicado a promos (no el de atención). Sumá varias líneas para repartir el envío y bajar el riesgo de bloqueo.</p>
          </div>
          <button onClick={addLine} disabled={loading} className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Agregar línea
          </button>
        </div>

        {/* Nota de riesgo (honestidad de producto): arriba, antes de la lista; estaba al final y se leía después de actuar */}
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4 flex items-start gap-3">
          <ShieldAlert size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800" data-testid="riesgo-lineas">
            El envío por WhatsApp no oficial va contra los términos de WhatsApp: hay riesgo de bloqueo del número.
            Por eso el envío es humanizado (goteo, pausas, ventanas horarias) y multi-número. Usá un número dedicado a promos, no el de atención.
          </p>
        </div>

        {/* Panel de conexión (selector de método → código o QR) */}
        {active && (
          <div className="rounded-3xl border-2 border-violet-200 bg-violet-50/40 p-6">
            {active.estado === "elegir" ? (
              /* Paso 1 — elegir método de vinculación */
              <div className="max-w-md mx-auto text-center">
                <p className="text-lg font-bold text-slate-900">{active.nombre}</p>
                <p className="text-sm text-slate-500 mt-1">Vinculá el WhatsApp del local con un <b>código</b> (recomendado) o escaneando un QR.</p>
                <div className="mt-4 text-left">
                  <label className="text-xs font-semibold text-slate-600">Número de WhatsApp (con código de país)</label>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="5491154596266"
                    className="mt-1 w-full text-sm rounded-xl border border-slate-200 px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-violet-200"
                  />
                  {active.lastError && <p className="text-xs text-red-600 mt-1">{active.lastError}</p>}
                </div>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <button onClick={() => pair(active)} className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700">
                    <KeyRound size={16} /> Vincular con código
                  </button>
                  <button onClick={() => connectQr(active)} className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl border border-slate-200 text-slate-600 text-sm font-medium hover:bg-white">
                    <QrCode size={16} /> Usar QR
                  </button>
                </div>
                {/* cancelar acá NO es sólo cerrar: si la línea se creó recién y no llegó a
                    vincularse, se borra — si no, queda un "sin vincular" que nadie pidió */}
                <button onClick={cancelarConexion} className="text-xs text-slate-400 hover:text-slate-600 mt-3">cancelar</button>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  {active.pairCode ? (
                    <div className="w-52 h-52 rounded-2xl bg-white border border-violet-200 flex flex-col items-center justify-center gap-2">
                      <KeyRound size={26} className="text-violet-400" />
                      <p className="text-3xl font-black tracking-[0.2em] text-slate-900 tabular-nums">{active.pairCode}</p>
                      <p className="text-[11px] text-slate-400 uppercase tracking-wider">código de vinculación</p>
                    </div>
                  ) : active.qr ? (
                    <img src={active.qr} alt="QR de WhatsApp" className="w-52 h-52 rounded-2xl bg-white p-2 border border-slate-200" />
                  ) : (
                    <div className="w-52 h-52 rounded-2xl bg-white border border-slate-200 flex items-center justify-center">
                      {active.estado === "activa" ? <CheckCircle2 size={48} className="text-emerald-500" /> : <Loader2 size={40} className="text-slate-300 animate-spin" />}
                    </div>
                  )}
                </div>
                <div className="flex-1 text-center md:text-left">
                  <p className="text-lg font-bold text-slate-900">{active.nombre}</p>
                  {active.estado === "activa" ? (
                    <>
                      <p className="text-sm text-emerald-700 mt-1 inline-flex items-center gap-1.5"><CheckCircle2 size={15} /> Conectada · {active.numero}</p>
                      <p className="text-xs text-slate-500 mt-2">La sesión queda guardada: no hace falta re-vincular la próxima vez.</p>
                    </>
                  ) : active.pairCode ? (
                    <>
                      <p className="text-sm font-semibold text-violet-800 mt-1 inline-flex items-center gap-1.5"><KeyRound size={15} /> Ingresá este código en tu teléfono</p>
                      <ol className="text-sm text-slate-600 mt-2 space-y-1 list-decimal list-inside">
                        <li>Abrí WhatsApp en el teléfono del local</li>
                        <li>Ajustes → Dispositivos vinculados → Vincular un dispositivo</li>
                        <li>Tocá <b>Vincular con número de teléfono</b></li>
                        <li>Escribí el código <b>{active.pairCode}</b></li>
                      </ol>
                      <p className="text-xs text-slate-400 mt-2">Tenés tiempo de sobra: el código no se apura como el QR.</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-violet-800 mt-1 inline-flex items-center gap-1.5"><QrCode size={15} /> Escaneá este QR</p>
                      <ol className="text-sm text-slate-600 mt-2 space-y-1 list-decimal list-inside">
                        <li>Abrí WhatsApp en el teléfono del local</li>
                        <li>Ajustes → Dispositivos vinculados → Vincular dispositivo</li>
                        <li>Apuntá al QR</li>
                      </ol>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Lista de líneas */}
        <div className="space-y-3">
          {lines.length === 0 && (
            <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
              Todavía no hay líneas. Agregá la primera y escaneá el QR.
            </div>
          )}
          {lines.map((l) => {
            const b = badge[l.estado] || badge.desconectada;
            return (
              <div key={l.id} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${l.estado === "activa" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                    <Smartphone size={20} />
                  </div>
                  {/* con un ancho mínimo, en el celular el estado y el botón bajan de renglón en vez de aplastar el número */}
                  <div className="flex-1 min-w-[10rem]">
                    <p className="text-sm font-bold text-slate-800">{l.nombre}</p>
                    <p className="text-xs text-slate-400">{l.numero || "sin vincular"} · hasta {l.msgsPorDia} mensajes por día</p>
                  </div>
                  <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>
                  {l.estado === "activa" ? (
                    <button onClick={() => disconnect(l)} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 h-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50"><LogOut size={14} /> Desconectar</button>
                  ) : (
                    <button onClick={() => startConnect(l)} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 h-9 rounded-lg bg-violet-600 text-white hover:bg-violet-700"><KeyRound size={14} /> Conectar</button>
                  )}
                  {/* sacar la línea: hasta ahora una línea entraba y no salía nunca */}
                  <button
                    onClick={() => borrarLinea(l)}
                    disabled={loading}
                    aria-label={`Sacar ${l.nombre}`}
                    title="Sacar esta línea"
                    className="w-9 h-9 rounded-lg border border-slate-200 text-slate-400 hover:text-red-600 hover:border-red-200 disabled:opacity-50 inline-flex items-center justify-center flex-shrink-0"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {/* prueba de envío por línea activa */}
                {l.estado === "activa" && (
                  <div className="mt-3 pt-3 border-t border-slate-50 flex items-center gap-2 flex-wrap">
                    <input value={testTo[l.id] || ""} onChange={(e) => setTestTo((p) => ({ ...p, [l.id]: e.target.value }))} placeholder="+54 9 11 ..." className="text-sm rounded-lg border border-slate-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-200 w-44" />
                    <button onClick={() => test(l)} className="inline-flex items-center gap-1.5 text-sm font-medium px-3 h-9 rounded-lg border border-violet-200 text-violet-700 hover:bg-violet-50"><Send size={14} /> Enviar prueba</button>
                    {testResult[l.id] && <span className="text-xs text-slate-500">{testResult[l.id]}</span>}
                  </div>
                )}
                {/* respuestas que no se pudieron atribuir a ninguna campaña: sin esto no se veían en
                    ningún lado. Plegado y a demanda: el número llega enmascarado desde el backend. */}
                {(() => {
                  const filas = sinCampana[l.id];
                  const abierta = !!sinCampanaAbierta[l.id];
                  return (
                    <div className="mt-3 pt-3 border-t border-slate-50">
                      <button
                        onClick={() => toggleSinCampana(l.id)}
                        aria-expanded={abierta}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-violet-700"
                      >
                        <MessageSquare size={13} /> Respuestas sin campaña{Array.isArray(filas) ? ` (${filas.length})` : ""}
                        <ChevronDown size={13} className={`transition-transform ${abierta ? "rotate-180" : ""}`} />
                      </button>
                      {abierta && (
                        <div className="mt-2">
                          {filas === "cargando" && <p className="text-xs text-slate-400 inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> cargando…</p>}
                          {filas === "error" && <p className="text-xs text-red-600">No se pudieron cargar las respuestas.</p>}
                          {Array.isArray(filas) && filas.length === 0 && <p className="text-xs text-slate-400">No llegaron respuestas fuera de una campaña.</p>}
                          {Array.isArray(filas) && filas.length > 0 && (
                            <div className="divide-y divide-slate-50">
                              {filas.map((f: any) => (
                                <div key={f.id} className="flex items-start gap-3 py-2 flex-wrap">
                                  <span className="font-mono text-xs text-slate-600 tabular-nums w-32 flex-shrink-0">{f.numero}</span>
                                  <span className="text-sm text-slate-700 flex-1 min-w-[160px] break-words">{f.respuesta}</span>
                                  {f.estado === "baja" && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-600">baja</span>}
                                  {f.createdAt && <span className="text-[11px] text-slate-400">{new Date(f.createdAt).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            );
          })}
          <button onClick={load} className="min-h-[44px] self-start rounded-xl px-3 text-sm font-semibold text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1.5"><RefreshCw size={14} /> Actualizar</button>
        </div>

        {/* Mensajes de prueba — tracking de entrega/lectura por número (✓ enviado · ✓✓ entregado · ✓✓ azul leído) */}
        {stats && stats.total > 0 && (
          <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-slate-800 inline-flex items-center gap-1.5"><CheckCheck size={15} className="text-sky-500" /> Mensajes de prueba</p>
              <div className="flex items-center gap-3 text-[11px] text-slate-400">
                <span>enviados <strong className="text-slate-600 tabular-nums">{stats.resumen.enviados}</strong></span>
                <span>entregados <strong className="text-slate-600 tabular-nums">{stats.resumen.entregados}</strong></span>
                <span>leídos <strong className="text-sky-600 tabular-nums">{stats.resumen.leidos}</strong></span>
                <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> vivo</span>
              </div>
            </div>
            <div className="divide-y divide-slate-50">
              {stats.pruebas.map((p: any, i: number) => (
                <div key={i} className="flex items-center gap-3 py-2.5 flex-wrap">
                  <span className="font-mono text-sm text-slate-700 tabular-nums w-40 flex-shrink-0">{p.numero}</span>
                  <span className="text-xs font-semibold flex-1 min-w-[120px]"><EstadoTick estado={p.estado} /></span>
                  {p.linea && <span className="text-[11px] text-slate-400">por {p.linea}</span>}
                  {p.leidoAt && <span className="text-[11px] text-slate-400">leído {new Date(p.leidoAt).toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}</span>}
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-3">
              El <strong className="text-sky-600">leído</strong> aparece cuando el destinatario abre el mensaje (✓✓ azul). Un mensaje a tu propio número queda en "enviado" (WhatsApp no da recibos a uno mismo).
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
